import { Injectable } from '@angular/core';
import {
  SKUApiItem,
  SKUItem,
  SKUDetail,
  SKUAttribute,
} from '../models/sku.interface';

@Injectable({
  providedIn: 'root',
})
export class SKUMapperService {
  /**
   * Maps SKUApiItem (new API format) to SKUItem (legacy format)
   */
  mapApiItemToLegacyItem(apiItem: SKUApiItem, serialNumber: number): SKUItem {
    return {
      id: serialNumber, // Use serial number as ID for legacy compatibility
      serialNumber,
      productCategory: apiItem.categoryName,
      productCode: apiItem.spuCode,
      productName: apiItem.spuName,
      skuCode: apiItem.skuCode,
      skuName: apiItem.skuName,
      materialUrl: apiItem.materialMainImages?.[0] || '',
      materialName: apiItem.materialName,
      costPrice: apiItem.costPrice,
      sellingPrice: apiItem.salesPrice,
      marketPrice: apiItem.marketPrice,
      settlementPrice: apiItem.clearingPrice,
      distributionPrice: apiItem.distributionPrice,
      internalAllocationPrice: 0, // Not available in new API
      marketingPrice: apiItem.activityPrice,
      wholesalePrice: apiItem.wholesalePrice,
      productWeight: apiItem.weight,
      productHeight: apiItem.height,
      productLength: apiItem.length,
      productWidth: apiItem.width,
      productBrand: '', // Not available in new API
      mainImages: apiItem.materialMainImages,
      detailImages: [], // Not available in new API
      packageSkus: [], // Not available in new API
      remarks: apiItem.goodsRemark || '',
      status: apiItem.status.toString(),
      skuType: this.mapSpuTypeToSkuType(apiItem.spuType),
      auditStatus: apiItem.auditIdea || '',
      auditComments: apiItem.auditIdea || '',
      skuAttributeList: apiItem.skuAttributeList || [],
      creationDate: apiItem.creationDate || apiItem.auditCommitDate || '',
      activityPrice: apiItem.activityPrice,
      clearingPrice: apiItem.clearingPrice,
      isPriceIncludeVAT: apiItem.isPriceIncludeVAT ?? null,
      taxId: apiItem.taxId ?? null,
      taxCode: apiItem.taxCode ?? null,
    };
  }

  /**
   * Maps SKUApiItem to SKUDetail format
   */
  mapApiItemToDetail(apiItem: SKUApiItem): SKUDetail {
    return {
      id: apiItem.id,
      spuId: apiItem.spuId,
      spuName: apiItem.spuName,
      spuCode: apiItem.spuCode,
      materialId: apiItem.materialId,
      materialName: apiItem.materialName,
      materialMainImages: apiItem.materialMainImages,
      imageList: apiItem.materialMainImages, // Use main images as image list
      skuCode: apiItem.skuCode,
      brandName: '', // Not available in new API
      skuName: apiItem.skuName,
      categoryName: apiItem.categoryName,
      showFlag: apiItem.showFlag,
      setFlag: apiItem.setFlag,
      goodsRemark: apiItem.goodsRemark,
      status: apiItem.status,
      costPrice: apiItem.costPrice,
      salesPrice: apiItem.salesPrice,
      activityPrice: apiItem.activityPrice,
      marketPrice: apiItem.marketPrice,
      distributionPrice: apiItem.distributionPrice,
      wholesalePrice: apiItem.wholesalePrice,
      clearingPrice: apiItem.clearingPrice,
      weight: apiItem.weight,
      length: apiItem.length,
      width: apiItem.width,
      height: apiItem.height,
      auditIdea: apiItem.auditIdea || '',
      skuAttributeList: apiItem.skuAttributeList || [],
      otherAttributeList: apiItem.otherAttributeList || [],
      skuDetailList: null, // Not available in new API
      isPriceIncludeVAT: apiItem.isPriceIncludeVAT ?? null,
      taxId: apiItem.taxId ?? null,
      taxCode: apiItem.taxCode ?? null,
    };
  }

  /**
   * Maps SKUDetail (API detail response) to SKUItem (legacy format)
   */
  mapDetailToItem(detail: SKUDetail): SKUItem {
    const item: SKUItem = {
      id: parseInt(detail.id),
      serialNumber: 0,
      productCategory: detail.categoryName,
      productCode: detail.spuCode,
      productName: detail.spuName,
      skuCode: detail.skuCode,
      skuName: detail.skuName,
      materialUrl: detail.materialMainImages?.[0] || '',
      materialName: detail.materialName,
      costPrice: detail.costPrice,
      sellingPrice: detail.salesPrice,
      marketPrice: detail.marketPrice,
      settlementPrice: detail.clearingPrice,
      distributionPrice: detail.distributionPrice,
      internalAllocationPrice: detail.activityPrice,
      marketingPrice: detail.activityPrice,
      wholesalePrice: detail.wholesalePrice,
      productWeight: detail.weight,
      productHeight: detail.height,
      productLength: detail.length,
      productWidth: detail.width,
      productBrand: detail.brandName,
      mainImages: detail.materialMainImages || [],
      detailImages: detail.imageList || [],
      packageSkus: [],
      remarks: detail.goodsRemark || '',
      status: detail.status.toString(),
      skuType: detail.setFlag === 1 ? 'package' : 'ordinary',
      auditStatus: detail.auditIdea || '',
      auditComments: detail.auditIdea || '',
      skuAttributeList: detail.skuAttributeList || [],
      isPriceIncludeVAT: detail.isPriceIncludeVAT ?? null,
      taxId: detail.taxId ?? null,
      taxCode: detail.taxCode ?? null,
    };
    item.barcodes = detail.barcodes || [];
    item.setFlag = detail.setFlag;
    item.skuDetailList = detail.skuDetailList || [];
    return item;
  }

  /**
   * Maps SKUFormData to SKUSearchPayload
   */
  mapFormDataToSearchPayload(
    formData: any,
    page: number = 0,
    size: number = 20,
  ): any {
    return {
      spuCode: formData.spuCode || formData.productCode || null,
      skuCode: formData.skuCode || null,
      spuName: formData.spuName || formData.productName || null,
      categoryIdList: formData.categoryIdList || [],
      status: this.mapStatusToNumbers(formData.status),
      setFlag: formData.setFlag || 0,
      page,
      size,
    };
  }

  /**
   * Maps legacy status format to number array
   */
  private mapStatusToNumbers(status: string | string[] | undefined): number[] {
    if (!status) return [1, 2, 3, 4, 5]; // Default to all statuses

    if (Array.isArray(status)) {
      return status.map((s) => parseInt(s, 10)).filter((n) => !isNaN(n));
    }

    return [parseInt(status, 10)].filter((n) => !isNaN(n));
  }

  /**
   * Maps SPU type number to SKU type string
   */
  private mapSpuTypeToSkuType(
    spuType: number,
  ): 'ordinary' | 'package' | 'draft' {
    switch (spuType) {
      case 1:
        return 'ordinary';
      case 2:
        return 'package';
      case 3:
        return 'draft';
      default:
        return 'ordinary';
    }
  }

  /**
   * Translates Chinese attribute names to English
   */
  translateAttributeName(attributeName: string): string {
    const translations: { [key: string]: string } = {
      宽度: 'Width',
      长度: 'Length',
      高度: 'Height',
      颜色: 'Color',
      尺寸: 'Size',
      材质: 'Material',
      品牌: 'Brand',
      型号: 'Model',
      重量: 'Weight',
      容量: 'Capacity',
      功率: 'Power',
      电压: 'Voltage',
      频率: 'Frequency',
      温度: 'Temperature',
      湿度: 'Humidity',
      压力: 'Pressure',
      速度: 'Speed',
      时间: 'Time',
      日期: 'Date',
      数量: 'Quantity',
    };

    return translations[attributeName] || attributeName;
  }

  /**
   * Translates Chinese text in the API response
   */
  translateApiResponse(apiItem: SKUApiItem): SKUApiItem {
    return {
      ...apiItem,
      materialName: this.translateMaterialName(apiItem.materialName),
      categoryName: this.translateCategoryName(apiItem.categoryName),
      skuAttributeList:
        apiItem.skuAttributeList?.map((attr) => ({
          ...attr,
          attributeName: this.translateAttributeName(attr.attributeName),
        })) || null,
    };
  }

  /**
   * Translates material names
   */
  private translateMaterialName(materialName: string): string {
    const translations: { [key: string]: string } = {
      测试素材: 'Test Material',
      待拍摄: 'Pending Photography',
    };

    return translations[materialName] || materialName;
  }

  /**
   * Translates category names
   */
  private translateCategoryName(categoryName: string): string {
    const translations: { [key: string]: string } = {
      '测试/1111': 'Test/1111',
      通用分类: 'General Category',
      '112': '112',
    };

    return translations[categoryName] || categoryName;
  }
}
